import React, { useState, useEffect } from "react";
import telegramupApi from "./telegramupApi";
import "./WalletModal.css";

export default function WithdrawModal({ balance, promoBalance = 0, onClose, onSuccess }) {
  const [wallet, setWallet] = useState("real");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("telebirr");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resultMsg, setResultMsg] = useState("");
  const [done, setDone] = useState(false);
  const [bp, setBp] = useState(null);
  const [avail, setAvail] = useState(null);

  useEffect(() => {
    telegramupApi.getWithdrawalAvailable().then(setAvail).catch(() => {});
  }, []);

  useEffect(() => {
    if (wallet === "promo" && !bp) {
      telegramupApi
        .getBestPlayerProgress()
        .then(setBp)
        .catch(() => setBp({ has_promo: false, qualified: false }));
    }
  }, [wallet]);

  const handleSubmit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setError("Please enter a valid amount"); return; }
    if (wallet === "real" && amt > parseFloat(balance)) { setError("Insufficient balance"); return; }
    if (!phone.trim() || phone.trim().length < 9) { setError("Please enter a valid phone number"); return; }
    setLoading(true);
    setError("");
    try {
      const fn = wallet === "promo" ? telegramupApi.initiatePromoWithdrawal : telegramupApi.initiateWithdrawal;
      const data = await fn(amt, method, phone.trim());
      setResultMsg(data.message);
      setDone(true);
      onSuccess && onSuccess();
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Failed to submit withdrawal. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const checklist = bp && [
    { label: `Invites: ${bp.invites ?? 0} / ${bp.invites_needed ?? 10}`, ok: (bp.invites ?? 0) >= (bp.invites_needed ?? 10) },
    { label: `Invited deposited: ${bp.invited_played ?? 0} / ${bp.invited_played_needed ?? 3}`, ok: (bp.invited_played ?? 0) >= (bp.invited_played_needed ?? 3) },
    { label: `Fresh deposit: ${(bp.deposit_available ?? 0).toFixed(2)} ETB`, ok: (bp.deposit_available ?? 0) > 0 },
  ];

  return (
    <div className="wm-overlay" onClick={onClose}>
      <div className="wm-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="wm-header">
          <h3>🏦 Withdraw</h3>
          <button className="wm-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="wm-error">{error}</div>}

        {!done ? (
          <div className="wm-body">
            <div className="wm-wallet-row">
              <button
                className={`wm-method-btn ${wallet === "real" ? "wm-method-btn--active" : ""}`}
                onClick={() => setWallet("real")}
              >
                💳 Balance
              </button>
              <button
                className={`wm-method-btn ${wallet === "promo" ? "wm-method-btn--active" : ""}`}
                onClick={() => setWallet("promo")}
              >
                🎁 Bonus
              </button>
            </div>

            {wallet === "real" && (
              <p className="wm-text">
                You can withdraw: <b>{(avail ? avail.available : parseFloat(balance)).toFixed(2)} ETB</b>
                {avail && avail.min_withdrawal ? ` · Min ${avail.min_withdrawal}` : " · Min 50"}
              </p>
            )}

            {wallet === "promo" && (
              <div className="wm-promo">
                <p className="wm-text">
                  Bonus balance: <b>{parseFloat(promoBalance).toFixed(2)} ETB</b>
                </p>
                {!bp && <p className="wm-text">Loading…</p>}
                {bp && !bp.has_promo && <p className="wm-text">No promo wallet.</p>}
                {bp && bp.has_promo && (
                  <>
                    {bp.qualified && (
                      <p className="wm-text" style={{ fontSize: "1.05rem", fontWeight: 800, color: "#b45309" }}>
                        🏆 Best Player
                      </p>
                    )}
                    <ul className="wm-checklist">
                      {checklist.map((c, i) => (
                        <li key={i} className={c.ok ? "wm-check-ok" : "wm-check-no"}>
                          {c.ok ? "✅" : "⏳"} {c.label}
                        </li>
                      ))}
                    </ul>
                    {bp.qualified ? (
                      <p className="wm-text wm-pool">
                        You can withdraw up to <b>{Math.min(500, (bp.deposit_available ?? 0) * 5, bp.pool_remaining ?? 0).toFixed(2)} ETB</b>
                        {" "}· uses <b>{(Math.min(500, (bp.deposit_available ?? 0) * 5, bp.pool_remaining ?? 0) * 0.2).toFixed(2)} ETB</b> of your deposit
                      </p>
                    ) : (
                      <p className="wm-text">Finish the checklist to unlock bonus withdrawal (deposit also required).</p>
                    )}
                  </>
                )}
              </div>
            )}

            {(wallet === "real" || (bp && bp.qualified)) && (
              <>
                <label className="wm-label">Amount (ETB)</label>
                <input
                  className="wm-input"
                  type="number"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />

                <label className="wm-label">Payment method</label>
                <div className="wm-method-row">
                  <button
                    className={`wm-method-btn ${method === "telebirr" ? "wm-method-btn--active" : ""}`}
                    onClick={() => setMethod("telebirr")}
                  >
                    📱 Telebirr
                  </button>
                  <button
                    className={`wm-method-btn ${method === "cbebirr" ? "wm-method-btn--active" : ""}`}
                    onClick={() => setMethod("cbebirr")}
                  >
                    🏦 CBE Birr
                  </button>
                </div>

                <label className="wm-label">Phone number to receive funds</label>
                <input
                  className="wm-input"
                  type="text"
                  placeholder="e.g. 0912345678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />

                <button
                  className="wm-btn wm-btn--primary"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? "Submitting..." : wallet === "promo" ? "Withdraw Bonus" : "Submit Withdrawal"}
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="wm-body">
            <div className="wm-success">✅ {resultMsg}</div>
            <button className="wm-btn wm-btn--primary" onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
